<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.common.user', 2), to: '/settings/users' },
      {
        label: isLoading
          ? 'Loading...'
          : user.basicDetails.id
            ? user.basicDetails.forename + ' ' + user.basicDetails.surname
            : 'Unknown User'
      }
    ]"
  >
    <template #right></template>
  </PageHeader>

  <div class="grid sm:grid-cols-1 md:grid-cols-4 gap-x-8">
    <div>
      <Tabs
        v-model="currentTab"
        :tabs="tabs"
        direction="vertical"
        type="pills-rail-block"
        class="mb-8 item"
      />
    </div>
    <div class="md:col-span-3">
      <!-- Loading Skeleton -->
      <div v-if="isLoading">
        <Card>
          <CardBody>
            <div class="mb-6 flex items-center justify-between">
              <div class="flex items-center gap-4">
                <Skeleton
                  shape="circle"
                  size="64"
                />
                <div class="flex flex-col gap-2">
                  <Skeleton
                    width="200px"
                    height="24px"
                  />
                  <Skeleton
                    width="150px"
                    height="16px"
                  />
                </div>
              </div>
              <div class="flex gap-2">
                <Skeleton
                  width="100px"
                  height="40px"
                  :border-radius="'var(--rounded-md)'"
                />
                <Skeleton
                  width="120px"
                  height="40px"
                  :border-radius="'var(--rounded-md)'"
                />
              </div>
            </div>
            <div class="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <div
                v-for="i in 6"
                :key="i"
                class="flex flex-col gap-2"
              >
                <Skeleton
                  width="120px"
                  height="16px"
                />
                <Skeleton
                  width="100%"
                  height="40px"
                  :border-radius="'var(--rounded-md)'"
                />
              </div>
            </div>
            <div class="mt-4">
              <Skeleton
                width="140px"
                height="40px"
                :border-radius="'var(--rounded-md)'"
              />
            </div>
          </CardBody>
        </Card>
      </div>

      <!-- Actual Content -->
      <div v-else>
        <div v-if="currentTab == 'basic'">
          <Card>
            <!-- Firstname, last name, email (readonly), pronouns, job title -->
            <CardBody>
              <Alert
                v-if="!isLoading && user.isVerified === false"
                severity="warning"
                icon="triangle-exclamation"
                title="Not Verified"
                message="This user's account is not verified. Click the button below to send another verification email."
                action-button
                action-button-text="Resend Verification Email"
                :show-dismiss-button="false"
                :show-close-button="false"
                class="mb-4"
                @action="sendVerificationEmail()"
              />
              <div class="mb-6 flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <Avatar
                    :url="user.basicDetails.profilePicUrl"
                    :text="user.basicDetails.initials"
                    size="xl"
                  />
                  <div>
                    <h2 class="text-xl font-semibold">
                      {{ user.basicDetails.forename }} {{ user.basicDetails.surname }}
                    </h2>
                  </div>
                </div>
                <div class="flex gap-2">
                  <Button
                    v-if="$can('delete_users')"
                    label="Delete"
                    severity="danger"
                    icon="trash"
                    :is-loading="deletingUser"
                    @click="handleDeleteUser"
                  />
                  <Button
                    v-if="user.isActive !== null"
                    :label="user.isActive ? 'Deactivate' : 'Reactivate'"
                    :severity="user.isActive ? 'warning' : 'success'"
                    :icon="user.isActive ? 'ban' : 'circle-check'"
                    :is-loading="togglingActiveState"
                    outlined
                    @click="handleToggleActiveState"
                  />
                </div>
              </div>
              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  :label="$t('ui.entities.profile.firstName')"
                  :is-required="v$.basicDetails.forename.required"
                >
                  <Input
                    v-model="user.basicDetails.forename"
                    :has-error="v$.basicDetails.forename.$error"
                  />
                  <InputError :has-error="v$.basicDetails.forename.$error">
                    {{ v$.basicDetails.forename.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.profile.lastName')"
                  :is-required="v$.basicDetails.surname.required"
                >
                  <Input
                    v-model="user.basicDetails.surname"
                    :has-error="v$.basicDetails.surname.$error"
                  />
                  <InputError :has-error="v$.basicDetails.surname.$error">
                    {{ v$.basicDetails.surname.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.comms.email')"
                  :is-required="v$.basicDetails.email.required"
                >
                  <Input
                    v-model="user.basicDetails.email"
                    :has-error="v$.basicDetails.email.$error"
                    :readonly="!$can('update_user_email')"
                  />
                  <InputError :has-error="v$.basicDetails.email.$error">
                    {{ v$.basicDetails.email.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.common.jobTitle')"
                  :is-required="v$.basicDetails.jobTitle.required"
                >
                  <Input
                    v-model="user.basicDetails.jobTitle"
                    :has-error="v$.basicDetails.jobTitle.$error"
                    :readonly="!$can('update_user_email')"
                  />
                  <InputError :has-error="v$.basicDetails.jobTitle.$error">
                    {{ v$.basicDetails.jobTitle.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.common.pronouns')"
                  :is-required="v$.basicDetails.pronouns.required"
                >
                  <Select
                    v-model="user.basicDetails.pronouns"
                    mode="single"
                    :options="['he/him', 'she/her', 'they/them']"
                    :has-error="v$.basicDetails.pronouns.$error"
                  ></Select>
                  <InputError :has-error="v$.basicDetails.pronouns.$error">
                    {{ v$.basicDetails.pronouns.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
              <Button
                label="Save Changes"
                type="primary"
                class="mt-4"
                :is-loading="formState.basicDetailsSubmitting"
                @click="submitBasicForm"
              />
            </CardBody>
          </Card>
        </div>

        <div v-if="currentTab == 'security'">
          <Card>
            <CardBody>
              <CardTitle title="Password Reset" />
              <p>Reset a user's password. They will be sent an email with a link to reset their password.</p>
              <Button
                label="Reset Password"
                type="primary"
                class="mt-6"
                :is-loading="sendResetPasswordIsSending"
                @click="sendResetPasswordEmail"
              />
            </CardBody>
          </Card>
          <Card
            v-if="isViewingOwnProfile"
            class="mt-6"
          >
            <CardBody>
              <CardTitle title="Active Sessions" />
              <p class="mb-6">
                Manage active sessions for this user. You can revoke individual sessions or all sessions at once.
              </p>
              <ListGroup v-if="sessions.length > 0">
                <ListGroupItem
                  v-for="session in sessions"
                  :key="session.id"
                  class="flex justify-between items-start"
                >
                  <div class="flex flex-col gap-2 flex-1">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2">
                        <div class="flex items-center gap-2">
                          <Icon
                            v-if="getBrowserIcon(getBrowserName(session.userAgent))"
                            :type="'fa-' + (getBrowserIcon(getBrowserName(session.userAgent))?.icon || '')"
                            :fa-style="getBrowserIcon(getBrowserName(session.userAgent))?.faStyle || 'fa-brands'"
                            class="text-tertiary"
                          />
                          <span class="text-sm font-medium">
                            {{ getBrowserName(session.userAgent) || 'Unknown Device' }}
                          </span>
                        </div>
                        <Chip
                          v-if="session.isCurrent"
                          label="Current Session"
                          severity="success"
                          size="xs"
                        />
                        <Chip
                          v-else
                          label="Active Session"
                          severity="default"
                          size="xs"
                        />
                      </div>
                      <div class="flex flex-wrap gap-4 text-sm text-quaternary">
                        <span>
                          <Icon
                            type="location-dot"
                            class="text-xs mr-1"
                          />
                          {{ session.location || 'Unknown Location' }}
                        </span>
                        <span v-if="session.ipAddress">
                          <Icon
                            type="network-wired"
                            class="text-xs mr-1"
                          />
                          {{ session.ipAddress }}
                        </span>
                        <span>
                          <Icon
                            type="clock"
                            class="text-xs mr-1"
                          />
                          Last used {{ formatTimeAgo(session.lastUsedAt) }}
                        </span>
                        <span>
                          <Icon
                            type="calendar"
                            class="text-xs mr-1"
                          />
                          Created {{ formatTimeAgo(session.createdAt) }}
                        </span>
                      </div>
                      <span
                        v-if="!getBrowserName(session.userAgent) && session.userAgent"
                        class="text-xs text-quaternary"
                      >
                        {{ session.userAgent }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-end">
                    <Button
                      v-if="!session.isCurrent"
                      label="Revoke"
                      severity="danger"
                      size="sm"
                      :is-loading="revokingSessionId === session.id"
                      @click.stop="revokeSession(session.id)"
                    />
                  </div>
                </ListGroupItem>
              </ListGroup>
              <div
                v-else-if="!sessionsLoading"
                class="text-center py-8 text-quaternary"
              >
                No active sessions found.
              </div>
              <div
                v-if="sessionsLoading"
                class="text-center py-8 text-quaternary"
              >
                Loading sessions...
              </div>
              <Button
                v-if="sessions.length > 0"
                label="Revoke All Sessions"
                severity="danger"
                class="mt-6"
                :is-loading="revokingAllSessions"
                @click="revokeAllSessions"
              />
            </CardBody>
          </Card>
          <!-- <Card>
          <CardBody>
            <CardTitle title="2-Factor Authentication" />
            <p>
              Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to log
              in. Learn more.
            </p>
            <Button label="Set Up 2FA" type="primary" class="mt-6" />
          </CardBody>
        </Card> -->
        </div>

        <div v-if="currentTab == 'roles-and-access' && !tabs.find(t => t.id == currentTab)?.isHidden">
          <Card>
            <CardBody class="flex flex-col gap-6">
              <div>
                <CardTitle title="Level & Roles" />
                <div class="grid md:grid-cols-2 gap-x-8">
                  <FormGroup
                    v-if="user.basicDetails.userType == 2"
                    :label="$t('ui.common.level')"
                    :is-required="v$.rolesAndAccessDetails.level.required"
                    no-margin
                  >
                    <Select
                      v-model="user.rolesAndAccessDetails.level"
                      mode="single"
                      :options="levelOptions"
                      :has-error="v$.rolesAndAccessDetails.level.$error"
                      @select="user.rolesAndAccessDetails.roles = []"
                    ></Select>
                    <InputError :has-error="v$.rolesAndAccessDetails.level.$error">
                      {{ v$.rolesAndAccessDetails.level.$errors[0]?.$message }}
                    </InputError>
                  </FormGroup>
                  <FormGroup
                    :label="$t('ui.common.role', 2)"
                    info="Roles define what the user is permitted to do<br><br>E.g. The Sales Executive role can't create users, but the Sales Manager role can"
                    :is-required="v$.rolesAndAccessDetails.roles.required"
                    no-margin
                  >
                    <Select
                      v-model="user.rolesAndAccessDetails.roles"
                      mode="multiple"
                      value-key="value"
                      label-key="label"
                      :options="roleOptions"
                      :has-error="v$.rolesAndAccessDetails.roles.$error"
                    ></Select>
                    <InputError :has-error="v$.rolesAndAccessDetails.roles.$error">
                      {{ v$.rolesAndAccessDetails.roles.$errors[0]?.$message }}
                    </InputError>
                  </FormGroup>
                </div>
              </div>
              <div>
                <CardTitle
                  title="Location Access"
                  class="mt-6"
                />
                <UserAccessSelector
                  v-model="user.rolesAndAccessDetails.access"
                  :is-required="v$.rolesAndAccessDetails.access.required"
                />
              </div>
              <div>
                <Button
                  label="Save Changes"
                  type="primary"
                  class="mt-4"
                  :is-loading="formState.rolesAndAccessDetailsSubmitting"
                  @click="submitLevelAndRoles"
                />
              </div>
            </CardBody>
          </Card>
        </div>

        <div v-if="currentTab == 'notifications'">
          <Card>
            <CardBody>
              <CardTitle title="Notifications" />

              <TableBasic
                bordered
                class="mt-6"
              >
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th>
                      <Icon
                        v-tippy="'App'"
                        class="text-lg"
                        type="laptop"
                      />
                    </th>
                    <th>
                      <Icon
                        v-tippy="'Email'"
                        class="text-lg"
                        type="envelope"
                      />
                    </th>
                    <th>
                      <Icon
                        v-tippy="'SMS'"
                        class="text-lg"
                        type="sms"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="cat in user.notifications">
                    <tr
                      v-if="cat.notifications.length > 0"
                      :key="cat.category"
                    >
                      <td
                        colspan="4"
                        class="bg-secondary"
                      >
                        <div class="font-bold">{{ cat.category }}</div>
                      </td>
                    </tr>
                    <tr
                      v-for="notification in cat.notifications"
                      :key="notification.id"
                    >
                      <td>
                        <div class="font-medium">{{ notification.name }}</div>
                        <div class="text-sm text-quaternary">{{ notification.description }}</div>
                      </td>
                      <td>
                        <Checkbox
                          v-if="notification.channels.push && notification.channels.push.isActive != null"
                          v-model="notification.channels.push.isActive"
                          :readonly="!notification.channels.push.isAllowed"
                        />
                      </td>
                      <td>
                        <Checkbox
                          v-if="notification.channels.email && notification.channels.email.isActive != null"
                          v-model="notification.channels.email.isActive"
                          :readonly="!notification.channels.email.isAllowed"
                        />
                      </td>
                      <td>
                        <Checkbox
                          v-if="notification.channels.sms && notification.channels.sms.isActive != null"
                          v-model="notification.channels.sms.isActive"
                          :readonly="!notification.channels.sms.isAllowed"
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>
              </TableBasic>
              <Button
                label="Save Changes"
                type="primary"
                class="mt-6"
                :is-loading="formState.notificationsDetailsSubmitting"
                @click="submitNotificationsForm"
              />
            </CardBody>
          </Card>
        </div>

        <div v-if="currentTab == 'schedule'">
          <Card>
            <CardBody>
              <CardTitle :title="$t('ui.entities.schedule.label')" />
              <Alert
                title="New Settings Coming Soon"
                message="Due to the introduction of Call Centers, these settings need re-working."
                :show-dismiss-button="false"
                :show-close-button="false"
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, email } from '@vuelidate/validators'
import { useAbility } from '@casl/vue'
import Select from '@/components/forms/Select.vue'
import Swal from 'sweetalert2'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { alertToast } from '@/utilities/notification'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Tabs from '@/components/tabs/Tabs.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Button from '@/components/button/Button.vue'
import TableBasic from '@/components/tables/TableBasic.vue'
import Icon from '@/components/icon/Icon.vue'
import Alert from '@/components/alert/Alert.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'

import UserAccessSelector from '@/components/unique/users/UserAccessSelector.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Chip from '@/components/chip/Chip.vue'
import { timeFrom } from '@/utilities/date-time'
import { parseUserAgent } from '@/utilities/string'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { can } = useAbility()

const authStore = useAuthStore()

const userRequest = useApiRequest()
const userSubmitFormRequest = useApiRequest()

/**
 * Checks if the logged-in user is viewing their own profile
 */
const isViewingOwnProfile = computed(() => {
  const loggedInUserId = authStore.details.id
  const viewedUserId = user.value.basicDetails.id
  if (!loggedInUserId || !viewedUserId) {
    return false
  }
  return String(loggedInUserId) === String(viewedUserId)
})

const user = ref({
  basicDetails: {
    id: null,
    userType: null,
    profilePicUrl: null,
    forename: null,
    surname: null,
    initials: null,
    email: null,
    pronouns: null,
    jobTitle: null
  },
  securityDetails: {
    password: null,
    passwordConfirm: null
  },
  notifications: [],
  rolesAndAccessDetails: {
    level: null,
    roles: [],
    access: null
  },
  schedule: {
    shiftTemplate: null
  },
  isVerified: null,
  isActive: null
})

function fetchUserData() {
  userRequest.send({ endpoint: '/v1/users/' + route.params.id, method: 'GET' }).then(response => {
    if (!response.success) {
      console.error(`Endpoint failed, error fetching data: /v1/users/${route.params.id}`)
      return
    }

    user.value.basicDetails = {
      id: response.data.id,
      userType: response.data.userType,
      profilePicUrl: response.data.profilePicUrl,
      forename: response.data.forename,
      surname: response.data.surname,
      initials: response.data.initials,
      email: response.data.email,
      pronouns: response.data.pronouns,
      jobTitle: response.data.jobTitle
    }
    user.value.securityDetails = {
      password: null,
      passwordConfirm: null
    }
    user.value.notifications = setNotifications(response.data.notifications)
    user.value.rolesAndAccessDetails = {
      level: response.data.level,
      roles: response.data.roles,
      access: {
        regions: response.data.access.regionsAll ? [] : response.data.access.regions,
        regionsAll: response.data.access.regionsAll,
        manufacturers: response.data.access.manufacturersAll ? [] : response.data.access.manufacturers,
        manufacturersAll: response.data.access.manufacturersAll,
        groups: response.data.access.groupsAll ? [] : response.data.access.groups,
        groupsAll: response.data.access.groupsAll,
        dealerships: response.data.access.dealershipsAll ? [] : response.data.access.dealerships,
        dealershipsAll: response.data.access.dealershipsAll
      }
    }
    user.value.schedule = {
      hasShifts: response.data.hasShifts,
      shiftTemplate: response.data.shiftTemplate
    }
    user.value.isVerified = response.data.isVerified
    user.value.isActive = response.data.isActive
  })
}
fetchUserData()

const rolesAndPermissionsRequest = useApiRequest()
const rolesAndPermissions = ref({
  roles: [],
  permissions: []
})

/**
 * Computed loading state - true when either user data or roles/permissions are loading
 */
const isLoading = computed(() => {
  return userRequest.isLoading.value || rolesAndPermissionsRequest.isLoading.value
})

try {
  rolesAndPermissionsRequest.send({ endpoint: '/v1/roles' }).then(response => {
    if (response.success) {
      rolesAndPermissions.value.roles = response.data.roles
      rolesAndPermissions.value.permissions = response.data.permissions
    } else {
      alertToast('Failed to fetch permissions', response.message, 'error')
    }
  })
} catch (err) {
  alertToast('Failed to fetch permissions', err.message, 'error')
}

const currentTab = ref(route.params.tab)
const tabs = ref([
  {
    id: 'basic',
    label: t('ui.common.basicInfo'),
    icon: 'user',
    to: '/settings/users/' + route.params.id
  },
  {
    id: 'security',
    label: t('ui.common.security'),
    icon: 'key',
    to: '/settings/users/' + route.params.id + '/security'
  },
  {
    id: 'notifications',
    label: t('ui.entities.notifications.label', 2),
    icon: 'bell',
    to: '/settings/users/' + route.params.id + '/notifications'
  },
  {
    id: 'roles-and-access',
    label: t('ui.common.rolesAndAccess'),
    icon: 'shield',
    to: '/settings/users/' + route.params.id + '/roles-and-access',
    isHidden: computed(() => {
      if (can('update_own_access')) {
        return false
      } else {
        return authStore.details.id == user.value.basicDetails.id
      }
    })
  },
  {
    id: 'schedule',
    label: t('ui.entities.schedule.label'),
    icon: 'calendar-days',
    to: '/settings/users/' + route.params.id + '/schedule',
    isHidden: computed(() => {
      if (user.value.basicDetails.userType != 1) {
        return true
      } else {
        return false
      }
    })
  }
])

// Set tab to 'Basic' if no tab is set
if (route.params.tab === '') {
  currentTab.value = 'basic'
} else {
  currentTab.value = route.params.tab
}

// If the user changes the tab, update the route
watch(
  () => currentTab.value,
  newTab => {
    currentTab.value = newTab
    // fetch tab route
    const tab = tabs.value.find(tab => tab.id === newTab)
    if (tab) {
      // console.log('Tab Change: ', tab)
      router.replace(tab.to)
    }
  }
)

const roleOptions = computed(() => {
  let rolesArray = rolesAndPermissions.value.roles
  const sessionUserLevel = authStore.details.level
  const userType = user.value.basicDetails.userType
  const userLevel = user.value.rolesAndAccessDetails.level
  const hasAdminRole = authStore.roles.includes(1)

  // Initial filtering based on sessionUserLevel
  if (sessionUserLevel && sessionUserLevel !== '') {
    rolesArray = rolesArray?.filter(role => role.level <= sessionUserLevel)
  }

  // Transform roles to options
  let options =
    rolesArray?.map(role => ({
      value: role.id,
      label: role.name,
      level: role.level,
      userType: role.userType,
      color: role.color
    })) || []

  // Further filtering based on user type
  if (userType === 1) {
    options = options.filter(role => role.userType === 1)
  } else {
    options = options.filter(role => role.level === userLevel && role.userType === userType)
  }

  // Remove admin role if the user doesn't have it
  if (!hasAdminRole) {
    options = options.filter(role => role.value !== 1)
  }

  return options
})

const levelOptions = computed(() => {
  const allOptions = [
    {
      value: 'region',
      label: 'Global',
      weight: 1
    },
    {
      value: 'manufacturer',
      label: 'Manufacturer',
      weight: 2
    },
    {
      value: 'group',
      label: 'Group',
      weight: 3
    },
    {
      value: 'dealership',
      label: 'Dealership',
      weight: 4
    }
  ]

  if (authStore.details.userType == 1) {
    return allOptions
  }

  const sessionUserLevelWeight = allOptions.find(option => option.value == authStore.details.level)?.weight

  return allOptions.filter(option => option.weight >= sessionUserLevelWeight)
})

// update state with structured notifications
function setNotifications(notifications) {
  const notificationCategories = [...new Set(notifications.map(notification => notification.category))]

  const computedNotifications = []

  notificationCategories.forEach(category => {
    const categoryNotifications = notifications.filter(notification => notification.category == category)
    computedNotifications.push({
      category: category,
      notifications: categoryNotifications
    })
  })

  return computedNotifications
}

const formState = ref({
  basicDetailsSubmitting: false,
  securityDetailsSubmitting: false,
  notificationsDetailsSubmitting: false,
  rolesAndAccessDetailsSubmitting: false
})

const inputRules = {
  basicDetails: {
    forename: {
      required
    },
    surname: {
      required
    },
    email: {
      required,
      email
    },
    pronouns: { required },
    jobTitle: { required }
  },
  securityDetails: {
    password: {
      required
    },
    passwordConfirm: {
      required
    }
  },
  rolesAndAccessDetails: {
    level: { required: requiredIf(() => user.value.basicDetails.userType == 2) },
    roles: { required },
    access: { required }
  }
}

const v$ = useVuelidate(inputRules, user)

async function submitBasicForm() {
  const isFormCorrect = await v$.value.basicDetails.$validate()
  // console.log('isFormCorrect: ', isFormCorrect)

  if (isFormCorrect) {
    formState.value.basicDetailsSubmitting = true

    userSubmitFormRequest
      .send({
        endpoint: '/v1/users/' + route.params.id,
        method: 'PATCH',
        data: {
          forename: user.value.basicDetails.forename,
          surname: user.value.basicDetails.surname,
          email: user.value.basicDetails.email,
          pronouns: user.value.basicDetails.pronouns,
          jobTitle: user.value.basicDetails.jobTitle
        }
      })
      .then(response => {
        if (response.success) {
          formState.value.basicDetailsSubmitting = false

          // Notification
          alertToast('Updated', null, 'success')
        }
      })
  }
}

async function submitNotificationsForm() {
  formState.value.notificationsDetailsSubmitting = true

  // sort data into correct format for PATCH request
  //   "notifications": [
  //     {
  //         "id": 1,
  //         "channels": {
  //             "email": false
  //         }
  //     }
  // ]
  const notifications = []

  user.value.notifications.forEach(category => {
    category.notifications.forEach(notification => {
      notifications.push({
        id: notification.notificationId,
        channels: {
          push: notification.channels.push?.isActive,
          email: notification.channels.email?.isActive,
          smm: notification.channels.sms?.isActive
        }
      })
    })
  })

  // console.log(notifications)

  userSubmitFormRequest
    .send({
      endpoint: '/v1/users/' + route.params.id + '/notifications/settings',
      method: 'PATCH',
      data: {
        notifications: notifications
      }
    })
    .then(response => {
      if (response.success) {
        formState.value.notificationsDetailsSubmitting = false

        // Notification
        alertToast('Updated', null, 'success')
      }
    })
}

async function submitLevelAndRoles() {
  const isFormCorrect = await v$.value.rolesAndAccessDetails.$validate()
  if (!isFormCorrect) {
    return
  }

  // if no dealerships selected, show warning
  if (
    !user.value.rolesAndAccessDetails.access.dealershipsAll &&
    !user.value.rolesAndAccessDetails.access.dealerships.length
  ) {
    Swal.fire({
      title: 'Are you sure?',
      text: 'The user currently has no access',
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes, save access',
      confirmButtonColor: 'var(--bg-error-solid)',
      cancelButtonText: 'Cancel'
    }).then(function (response) {
      if (response.isConfirmed) {
        saveLevelAndRoles()
      }
    })
  } else {
    saveLevelAndRoles()
  }
}

async function saveLevelAndRoles() {
  formState.value.rolesAndAccessDetailsSubmitting = true

  // Submit level and roles to user PATCH endpoint
  userSubmitFormRequest
    .send({
      endpoint: '/v1/users/' + route.params.id,
      method: 'PATCH',
      data: {
        roleIds: user.value.rolesAndAccessDetails.roles
      }
    })
    .then(response => {
      if (response.success) {
        // If successful, format and submit access to user/access PATCH endpoint
        const userAccess = user.value.rolesAndAccessDetails.access
        const newAccessData = {
          level: user.value.rolesAndAccessDetails.level,
          regions: userAccess.regionsAll ? '*' : userAccess.regions.length === 0 ? null : userAccess.regions,
          manufacturers: userAccess.manufacturersAll
            ? '*'
            : userAccess.manufacturers.length === 0
              ? null
              : userAccess.manufacturers,
          groups: userAccess.groupsAll ? '*' : userAccess.groups.length === 0 ? null : userAccess.groups,
          dealerships: userAccess.dealershipsAll
            ? '*'
            : userAccess.dealerships.length === 0
              ? null
              : userAccess.dealerships
        }

        // console.log(newAccessData)

        userSubmitFormRequest
          .send({
            endpoint: '/v1/users/' + route.params.id + '/access',
            method: 'PUT',
            data: newAccessData
          })
          .then(response => {
            if (response.success) {
              formState.value.rolesAndAccessDetailsSubmitting = false

              // Notification
              alertToast('Updated', null, 'success')
            }
          })
      }
    })
}

const sendResetPasswordIsSending = ref(false)

// Sessions management
interface Session {
  id: number
  ipAddress: string
  userAgent: string
  location: string | null
  createdAt: string
  lastUsedAt: string
  expiresAt: string
  isCurrent: boolean
}

const sessionsRequest = useApiRequest()
const sessions = ref<Session[]>([])
const sessionsLoading = ref(false)
const revokingSessionId = ref<number | null>(null)
const revokingAllSessions = ref(false)

/**
 * Fetches the list of active sessions for the user
 */
function fetchSessions() {
  sessionsLoading.value = true
  sessionsRequest
    .send({ endpoint: '/v1/auth/sessions', method: 'GET' })
    .then(response => {
      if (response.success && response.data) {
        const apiSessions = Array.isArray(response.data) ? response.data : []
        // Map snake_case API response to camelCase
        sessions.value = apiSessions.map((session: any) => ({
          id: session.id,
          ipAddress: session.ip_address || session.ipAddress || '',
          userAgent: session.user_agent || session.userAgent || '',
          location: session.location || null,
          createdAt: session.created_at || session.createdAt || '',
          lastUsedAt: session.last_used_at || session.lastUsedAt || '',
          expiresAt: session.expires_at || session.expiresAt || '',
          isCurrent: session.is_current !== undefined ? session.is_current : session.isCurrent || false
        }))
      } else {
        sessions.value = []
      }
    })
    .catch(() => {
      sessions.value = []
    })
    .finally(() => {
      sessionsLoading.value = false
    })
}

/**
 * Revokes a specific session
 * @param sessionId - The ID of the session to revoke
 */
async function revokeSession(sessionId: number) {
  revokingSessionId.value = sessionId
  const revokeRequest = useApiRequest()
  try {
    const response = await revokeRequest.send({
      endpoint: `/v1/auth/sessions/${sessionId}`,
      method: 'DELETE'
    })
    if (response.success) {
      alertToast('Session revoked', null, 'success')
      fetchSessions()
    } else {
      alertToast('Failed to revoke session', response.message || 'Unknown error', 'error')
    }
  } catch (err: unknown) {
    alertToast('Failed to revoke session', err instanceof Error ? err.message : 'Unknown error', 'error')
  } finally {
    revokingSessionId.value = null
  }
}

/**
 * Revokes all sessions for the user
 */
async function revokeAllSessions() {
  revokingAllSessions.value = true
  const revokeAllRequest = useApiRequest()
  try {
    const response = await revokeAllRequest.send({
      endpoint: '/v1/auth/sessions',
      method: 'DELETE'
    })
    if (response.success) {
      alertToast('All sessions revoked', null, 'success')
      fetchSessions()
    } else {
      alertToast('Failed to revoke all sessions', response.message || 'Unknown error', 'error')
    }
  } catch (err: unknown) {
    alertToast('Failed to revoke all sessions', err instanceof Error ? err.message : 'Unknown error', 'error')
  } finally {
    revokingAllSessions.value = false
  }
}

/**
 * Formats a timestamp as a relative time string (e.g., "2 hours ago")
 * @param timestamp - The timestamp to format
 * @returns Formatted relative time string
 */
function formatTimeAgo(timestamp: string): string {
  return timeFrom(timestamp)
}

/**
 * Gets the browser name from a user agent string
 * @param userAgent - The user agent string
 * @returns The browser name or null if not identifiable
 */
function getBrowserName(userAgent: string | null | undefined): string | null {
  return parseUserAgent(userAgent)
}

/**
 * Gets the Font Awesome icon name and style for a browser
 * @param browserName - The browser name (e.g., "Chrome", "Firefox")
 * @returns An object with icon name and faStyle, or null if no icon available
 */
function getBrowserIcon(browserName: string | null): { icon: string; faStyle: string } | null {
  if (!browserName) {
    return null
  }

  // Brand icons (use fa-brands style)
  const brandIconMap: Record<string, string> = {
    Chrome: 'chrome',
    Firefox: 'firefox-browser',
    Safari: 'safari',
    Edge: 'edge',
    Opera: 'opera',
    'Internet Explorer': 'internet-explorer'
  }

  // Regular icons (use fa-solid style) for browsers without brand icons
  const regularIconMap: Record<string, string> = {
    Brave: 'shield-halved',
    'Samsung Internet': 'mobile-screen-button',
    Vivaldi: 'globe'
  }

  if (brandIconMap[browserName]) {
    return {
      icon: brandIconMap[browserName],
      faStyle: 'fa-brands'
    }
  }

  if (regularIconMap[browserName]) {
    return {
      icon: regularIconMap[browserName],
      faStyle: 'fa-solid'
    }
  }

  return null
}

// Fetch sessions when security tab is active and viewing own profile
watch(
  [() => currentTab.value, () => isViewingOwnProfile.value],
  ([newTab, isOwnProfile]) => {
    // Clear sessions when viewing someone else's profile
    if (!isOwnProfile) {
      sessions.value = []
      return
    }
    // Fetch sessions when on security tab and viewing own profile
    if (newTab === 'security' && isOwnProfile && !sessionsLoading.value) {
      fetchSessions()
    }
  },
  { immediate: true }
)

async function sendResetPasswordEmail() {
  sendResetPasswordIsSending.value = true
  const resetPasswordRequest = useApiRequest()
  resetPasswordRequest
    .send({
      endpoint: '/v1/auth/forgot-password',
      method: 'POST',
      data: { email: user.value.basicDetails.email }
    })
    .then(response => {
      if (response.success) {
        alertToast('Email sent', null, 'success')
      } else {
        alertToast('Failed to reset password', response.message, 'error')
      }
    })
    .catch(err => {
      alertToast('Failed to reset password', err.message, 'error')
    })
    .finally(() => {
      sendResetPasswordIsSending.value = false
    })
}

const verificationEmailRequest = useApiRequest()

function sendVerificationEmail() {
  const dataToPost = JSON.stringify({ email: user.value.basicDetails.email })
  try {
    verificationEmailRequest
      .send({ method: 'POST', endpoint: '/v1/auth/resend-verification', data: dataToPost })
      .then(response => {
        if (response.success) {
          alertToast('Email sent', null, 'success')
        } else {
          alertToast('Failed to send email', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to send email', err.message, 'error')
  }
}

// Watch for changes in the id in the route, and if it changes, fetch the new user data
watch(
  () => route.params.id,
  () => {
    fetchUserData()
  }
)

const deletingUser = ref(false)
const togglingActiveState = ref(false)

/**
 * Handles deleting a user after confirmation
 */
async function handleDeleteUser() {
  const userName =
    user.value.basicDetails.forename && user.value.basicDetails.surname
      ? `${user.value.basicDetails.forename} ${user.value.basicDetails.surname}`
      : 'this user'

  const result = await Swal.fire({
    title: 'Are you sure?',
    html: `
      <p>You are about to delete <strong>${userName}</strong>.</p>
      <p class="mt-3"><strong>This action cannot be undone.</strong></p>
      <p class="mt-2">All user data, including their account, settings, and associated records will be permanently deleted.</p>
    `,
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete this user',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  })

  if (result.isConfirmed) {
    deletingUser.value = true
    const deleteRequest = useApiRequest()
    try {
      const response = await deleteRequest.send({
        endpoint: '/v1/users/' + route.params.id,
        method: 'DELETE'
      })
      if (response.success) {
        alertToast('User deleted', null, 'success')
        router.push('/settings/users')
      } else {
        alertToast('Failed to delete user', response.message || 'Unknown error', 'error')
      }
    } catch (err: unknown) {
      alertToast('Failed to delete user', err instanceof Error ? err.message : 'Unknown error', 'error')
    } finally {
      deletingUser.value = false
    }
  }
}

/**
 * Handles toggling user active state (deactivate/reactivate)
 */
async function handleToggleActiveState() {
  const action = user.value.isActive ? 'deactivate' : 'reactivate'
  const newActiveState = action === 'reactivate'
  togglingActiveState.value = true
  const toggleRequest = useApiRequest()
  try {
    const response = await toggleRequest.send({
      endpoint: '/v1/users/' + route.params.id + '/' + action,
      method: 'PATCH'
    })
    if (response.success) {
      // Update the isActive state based on the action taken
      user.value.isActive = newActiveState
      alertToast('Updated', null, 'success')
    } else {
      alertToast('Failed to update user', response.message || 'Unknown error', 'error')
    }
  } catch (err: unknown) {
    alertToast('Failed to update user', err instanceof Error ? err.message : 'Unknown error', 'error')
  } finally {
    togglingActiveState.value = false
  }
}
</script>
